import { gql } from '@apollo/client'
import { Container, Grid } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { PropsWithChildren } from 'react'
import { Disclaimer } from '../../components/disclaimer/disclaimer'
import { GlossaryBreadcrumb } from '../../components/glossary/GlossaryBreadcrumb'
import { GlossaryCard } from '../../components/glossary/GlossaryCard'
import { ContactCtaFragment } from '../../fragments/__generated__/contactCta'
import { CONTACT_CTA_FRAGMENT, ContactCta } from '../../fragments/contactCta'
import { IMAGE_FRAGMENT } from '../../fragments/image'
import { GLOBAL_LINKS_CTA_FRAGMENT } from '../../fragments/linksCtaFallback'
import { html } from '../../utils/htmlParser'
import { isNotNull, nonNull } from '../../utils/nonNull'
import { PopularGuideArticlesFragment } from '../faq/fragments/__generated__/PopularGuideArticles'
import { GlossaryPageQuery } from './__generated__'
import {
  GLOSSARY_BANNER_FRAGMENT,
  POPULAR_GUIDE_ARTICLES_FRAGMENT,
  PopularGuideArticles,
  SecondaryPageBanner,
} from './fragments'
import { GlossaryBannerFragment } from './fragments/__generated__/glossaryBanner'
import {
  GlossaryImageWrapper,
  GlossaryWrapper,
  StyledContactCTAWrapper,
  StyledDisclaimerWrapper,
  StyledGlossaryContentWrapper,
  StyledGlossaryHeader,
  StyledGlossaryPageContainer,
  StyledGreyBackground,
  StyledPopularGuideArticlesHeader,
  StyledPopularGuideArticlesWrapper,
} from './styledGlossaryPage'

export type GlossaryProps = GlossaryPageQuery

export const Glossary: React.FC<PropsWithChildren<GlossaryProps>> = ({ entry, entries, linksCtaFallback }) => {
  if (!entry || entry.__typename !== 'glossaryPage_glossaryPage_Entry') {
    return null
  }

  const letters = entries ? nonNull(entries).map((segment) => {
    if(segment.__typename === 'glossary_default_Entry') {
      return segment.title
    }

    return null
  }).sort() : []

  type ImageArray = {
    alt?: string
    url?:string
    __typename?: string
  }

  type ContentArray = {
    htmlContent?: string
  }

  const glossaryItems = entries && entries.length > 0 && nonNull(entries).map((segment) => {
    if (segment && segment.__typename === 'glossary_default_Entry') {
      return {
        letter: segment.title,
        itemImage: segment.commonSimpleImage,
        itemContent: segment.glossaryItems,
      }
    }

    return null
  }) as ({ letter: string; itemImage:ImageArray[]; itemContent: ContentArray[]}[])

  const sortedItems = glossaryItems && nonNull(glossaryItems).sort((a,b) => a.letter.localeCompare(b.letter))

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const entryBanner = entry.glossaryBanner && entry.glossaryBanner.length > 0 ?
    (entry.glossaryBanner[0] as GlossaryBannerFragment) : null
  const entryContactCta = entry.contactCta
    ? entry.contactCta as ContactCtaFragment[]
    : null
  const popularGuideArticles = entry.popularGuideArticles
    ? entry.popularGuideArticles as PopularGuideArticlesFragment[]
    : null

  // Add smooth scrolling when clicking on glossary letters
  return (
    <StyledGlossaryPageContainer>
      {
        entryBanner !== null &&
          <SecondaryPageBanner
            banner={entryBanner}
          />
      }
      <StyledGreyBackground maxWidth={false}>
        <Container disableGutters={true}>
          <Grid direction={'column'} alignItems={'center'} container>
            {/** @ts-ignore **/}
            <StyledGlossaryContentWrapper item xs={12} lg={10} component={'section'}>
              <StyledGlossaryHeader variant="h4">
                  From ‘borrowing capacity’ to ‘vendor’, this glossary will help you navigate harder to understand terminology when managing your finances.
              </StyledGlossaryHeader>
              <GlossaryWrapper>
                {sortedItems && sortedItems.map((segment, index) => {
                  return (
                    isNotNull(segment.letter) && <div id={segment.letter} key={index}>
                      <GlossaryBreadcrumb letters={nonNull(letters)} selectedLetter={segment.letter} />
                      {isMobile && (<GlossaryCard hr />)}
                      <GlossaryCard >
                        <GlossaryCard title>{segment.letter}</GlossaryCard>
                        <GlossaryCard segment>
                          {segment.itemContent && segment.itemContent.length > 0 && segment.itemContent.map((section, key) => {
                            return (
                              <GlossaryCard wrapper key={key}>
                                {key === 0 &&  isMobile ? null : (<GlossaryCard hr />)}
                                {key === 0 && segment?.itemImage?.[0]?.url && (
                                  <GlossaryImageWrapper image src={segment.itemImage[0].url} />
                                )}
                                {section.htmlContent && <GlossaryCard content>{html(section.htmlContent)}</GlossaryCard>}
                              </GlossaryCard>
                            )
                          })}
                        </GlossaryCard>
                      </GlossaryCard>
                    </div>
                  )
                })}
              </GlossaryWrapper>
            </StyledGlossaryContentWrapper>
            {
              entryContactCta !== null && linksCtaFallback?.__typename === 'ctasProductPages_GlobalSet' &&
              <React.Fragment>
                {/** @ts-ignore **/}
                <StyledContactCTAWrapper item component={'section'} lg={10}>
                  <ContactCta data={nonNull(entryContactCta)} fallback={linksCtaFallback} />
                </StyledContactCTAWrapper>
              </React.Fragment>
            }
          </Grid>
        </Container>
      </StyledGreyBackground>

      <Container>
        <Grid direction={'column'} alignItems={'center'} container>
          {/** @ts-ignore **/}
          <StyledPopularGuideArticlesWrapper item xs={12} sm={12} component={'section'}>
            <StyledPopularGuideArticlesHeader variant="h5">
              Popular guides and articles
            </StyledPopularGuideArticlesHeader>
            {popularGuideArticles !== null && <PopularGuideArticles articles={nonNull(popularGuideArticles)}/>}
          </StyledPopularGuideArticlesWrapper>
          {/** @ts-ignore **/}
          {entry?.disclaimer && <StyledDisclaimerWrapper item xs={12} sm={12} component={'section'}>
            <Disclaimer>
              <Disclaimer title>Information to know</Disclaimer>
              <Disclaimer textBlock>
                {entry.disclaimer && html(entry.disclaimer)}
              </Disclaimer>
            </Disclaimer>
          </StyledDisclaimerWrapper>
          }
        </Grid>
      </Container>
    </StyledGlossaryPageContainer>
  )
}

export const GLOSSARYPAGE_QUERY = gql`
  ${GLOSSARY_BANNER_FRAGMENT}
  ${IMAGE_FRAGMENT}
  ${CONTACT_CTA_FRAGMENT}
  ${POPULAR_GUIDE_ARTICLES_FRAGMENT}
  ${GLOBAL_LINKS_CTA_FRAGMENT}

  query GlossaryPageQuery($uid: [String]) {
    entry(section: "glossaryPage", uid: $uid) {
      id
      title
      ... on glossaryPage_glossaryPage_Entry {
        glossaryBanner {
          ...GlossaryBannerFragment
        }
        contactCta {
          ...ContactCtaFragment
        }
        popularGuideArticles {
          ...PopularGuideArticlesFragment
        }
        disclaimer: simpleText
      }
    }
    entries(section: "glossary") {
      ... on glossary_default_Entry {
        title
        commonSimpleImage {
          ...ImageFragment
        }
        glossaryItems {
          ... on glossaryItems_BlockType {
            htmlContent
          }
        }
      }
    }
    linksCtaFallback: globalSet(handle: "ctasProductPages") {
      ...globalLinksCtaFragment
    }
  }
`
